import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, MapPin, Globe, Star, Briefcase, ThumbsUp, ThumbsDown, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, onChange, size = 'md' }) {
  const sz = size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange?.(n)} className={onChange ? 'cursor-pointer' : 'cursor-default'}>
          <Star className={`${sz} ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
}

export default function EmployerProfilePage() {
  const { employerId } = useParams();
  const { isAuthenticated, api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 0, title: '', content: '', pros: '', cons: '' });

  useEffect(() => { fetchProfile(); }, [employerId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employers/${employerId}/profile`);
      setData(res.data);
    } catch { toast.error('Failed to load employer profile'); }
    finally { setLoading(false); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (reviewForm.rating === 0) { toast.error('Please select a rating'); return; }
    if (!reviewForm.content.trim()) { toast.error('Please write a review'); return; }
    setSubmitting(true);
    try {
      await api.post('/reviews', {
        target_type: 'employer',
        target_id: employerId,
        rating: reviewForm.rating,
        title: reviewForm.title,
        content: reviewForm.content,
        pros: reviewForm.pros.split('\n').map(s=>s.trim()).filter(Boolean),
        cons: reviewForm.cons.split('\n').map(s=>s.trim()).filter(Boolean),
      });
      toast.success('Review submitted — thank you!');
      setShowReviewModal(false);
      setReviewForm({ rating: 0, title: '', content: '', pros: '', cons: '' });
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit review'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#003366]" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><p>Employer not found</p></div>;

  const { employer, reviews, average_rating, review_count, active_jobs } = data;

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#003366] to-[#004080] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              {employer.logo_url ? <img src={employer.logo_url} alt={employer.company_name} className="w-16 h-16 object-contain rounded" /> : <Building2 className="w-10 h-10 text-white/70" />}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{employer.company_name || employer.full_name}</h1>
              <div className="flex flex-wrap gap-4 mt-2 text-white/70 text-sm">
                {employer.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{employer.location}</span>}
                {employer.website && <a href={employer.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white"><Globe className="w-4 h-4" />{employer.website}</a>}
                {employer.industry && <Badge className="bg-white/10 text-white border-white/20">{employer.industry}</Badge>}
              </div>
              <div className="flex items-center gap-3 mt-3">
                <StarRating value={Math.round(average_rating)} />
                <span className="text-white/80 text-sm">{average_rating > 0 ? average_rating.toFixed(1) : 'No ratings yet'} ({review_count} review{review_count !== 1 ? 's' : ''})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* About */}
        {employer.about_us && (
          <Card>
            <CardHeader><CardTitle>About</CardTitle></CardHeader>
            <CardContent><p className="text-muted-foreground leading-relaxed">{employer.about_us}</p></CardContent>
          </Card>
        )}

        {/* Active Jobs */}
        {active_jobs?.length > 0 && (
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5" />Open Positions ({active_jobs.length})</h2>
            <div className="space-y-3">
              {active_jobs.map(job => (
                <Card key={job.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{job.title}</h3>
                        <p className="text-sm text-muted-foreground">{job.location} · {job.job_type?.replace('_',' ')}</p>
                      </div>
                      <Link to={`/jobs/${job.id}`}><Button size="sm" className="bg-[#003366] hover:bg-[#003366]/90">Apply</Button></Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Reviews ({review_count})</h2>
            {isAuthenticated && (
              <Button onClick={() => setShowReviewModal(true)} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
                <Plus className="w-4 h-4 mr-2" />Write a Review
              </Button>
            )}
          </div>

          {reviews.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No reviews yet. Be the first to share your experience!</CardContent></Card>
          ) : reviews.map(r => (
            <Card key={r.id} className="mb-4">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <StarRating value={r.rating} />
                    {r.title && <h3 className="font-semibold mt-1">{r.title}</h3>}
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('en-GB')}</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">{r.content}</p>
                {r.pros?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1 mb-1"><ThumbsUp className="w-3 h-3" />Pros</p>
                    <ul className="text-sm text-gray-600 space-y-0.5">{r.pros.map((p,i)=><li key={i}>• {p}</li>)}</ul>
                  </div>
                )}
                {r.cons?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-500 flex items-center gap-1 mb-1"><ThumbsDown className="w-3 h-3" />Cons</p>
                    <ul className="text-sm text-gray-600 space-y-0.5">{r.cons.map((c,i)=><li key={i}>• {c}</li>)}</ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Write a Review</DialogTitle></DialogHeader>
          <form onSubmit={submitReview} className="space-y-4">
            <div className="space-y-2">
              <Label>Overall Rating *</Label>
              <StarRating value={reviewForm.rating} onChange={r => setReviewForm(f=>({...f, rating:r}))} size="lg" />
            </div>
            <div className="space-y-2">
              <Label>Review Title</Label>
              <Input value={reviewForm.title} onChange={e=>setReviewForm(f=>({...f,title:e.target.value}))} placeholder="Summarise your experience" />
            </div>
            <div className="space-y-2">
              <Label>Review *</Label>
              <Textarea value={reviewForm.content} onChange={e=>setReviewForm(f=>({...f,content:e.target.value}))} rows={4} placeholder="Share your experience working with this employer..." required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pros (one per line)</Label>
                <Textarea value={reviewForm.pros} onChange={e=>setReviewForm(f=>({...f,pros:e.target.value}))} rows={3} placeholder="Great culture&#10;Good pay&#10;Remote work" />
              </div>
              <div className="space-y-2">
                <Label>Cons (one per line)</Label>
                <Textarea value={reviewForm.cons} onChange={e=>setReviewForm(f=>({...f,cons:e.target.value}))} rows={3} placeholder="Long hours&#10;No progression" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowReviewModal(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-[#003366] hover:bg-[#003366]/90">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Submit Review
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
